<template>
  <div class="company-expiryEnquiry">
    <div class="title">
      <h3>{{ $t(`company.menu.label.expiryEnquiry`) }}</h3>
      <el-divider />
      <el-card>
        <h4 class="nomargin">{{ $t('company.expiryEnquiry.type.label') }}</h4>
        <el-radio-group v-model="enquiryType">
          <el-radio v-for="option in $tm('company.expiryEnquiry.type.options')" v-bind:key="option.value" :value="option.value">{{ option.label }}</el-radio>
        </el-radio-group>
        <br/>
        <el-form>
          <el-form-item :label="selectedTypeLabel" :class="[isInvalid(v$.enquiryValue.$error)]">
            <el-input class="enquiry-value-input" 
              :maxlength="inputLimit" 
              v-model="enquiryValue" 
              @focus="v$.enquiryValue.$reset()" 
              @blur="onBlur($event)"
            />
            <div class="invalid-msg" v-if="v$.enquiryValue.$errors.length">
              {{ v$.enquiryValue.$errors[0].$message }}
            </div>
          </el-form-item>
        </el-form>

        <div class="table-footer">
          <el-button style="margin-right:20px;" type="danger" size="large" plain @click="handleClear">
            <el-icon class="el-icon--left">
              <i class="bi bi-eraser-fill"></i>
            </el-icon>
            <span>{{ $t("common.button.clear") }}</span>
          </el-button>
          <el-button type="primary" @click="search">
            <el-icon>
              <i class="bi bi-search"></i>
            </el-icon>
            <span>{{ $t("common.button.search") }}</span>
          </el-button>
        </div>

        <div v-if="result.queryNum && result.expiry && result.queryType != null">
          <el-divider />
          <h4>
            {{ getSelectedTypeLabel(result.queryType) }}: {{ result.queryNum }}
          </h4>
          <h4>
            {{ $t("company.expiryEnquiry.result.label.expiry") }}: {{ formatDT(result.expiry) }}
          </h4>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import * as validators from '@/libs/i18n-validators'
import constants from "@/libs/constants";
import { triggerValidate, isInvalid } from "@/libs/common";
import renewalAPI from "@/http/apis/company/renewal";
import { WarningAlert } from "@/plugins/message";
import * as loading from "@/plugins/loading";
import { handleCompanyException } from "../../libs/common";
import moment from "moment";
import { useCompanyStore } from "@/store/company";

export default {
  name: "CompanyExpiryEnquiry",
  setup() {
    return {
      v$: useVuelidate({
        $lazy: true,
        $autoDirty: false,
      }),
      companyStore: useCompanyStore()
    };
  },
  validations() {
    return {
      enquiryValue: this.getEnquiryValueRules()
    }
  },
  computed: {
    selectedTypeLabel() {
      return this.getSelectedTypeLabel(this.enquiryType);
    },
    inputLimit() {
      let limit = 40;
      switch(this.enquiryType) {
        case 0:
          limit = 18;
          break;
        case 1:
          limit = 9;
          break;
      }
      return limit;
    }
  },
  data() {
    return {
      enquiryType: 0,
      enquiryValue: null,
      result: this.defaultResultObj()
    }
  },
  methods: {
    isInvalid(res) {
      return isInvalid(res);
    },
    getEnquiryValueRules() {
      let rules = {
        required: validators.required,
      }
      switch(this.enquiryType) {
        case 0:
          rules.checkLang = validators.checkLang({ lang: "E", allowNumber: true, allowSpacing: false, allowSymbol: false });
          rules.minLength = validators.minLength({
            limit: { num: 4 },
            unit: { num: constants.unit.digit }
          });
          rules.maxLength = validators.maxLength({
            limit: { num: 18 },
            unit: { num: constants.unit.character }
          });
          break;
        case 1:
          rules.numeric = validators.numeric;
          rules.minLength = validators.minLength({
            limit: { num: 8 },
            unit: { num: constants.unit.digit }
          });
          rules.maxLength = validators.maxLength({
            limit: { num: 9 },
            unit: { num: constants.unit.digit }
          });
          break;
      }

      return rules;
    },
    onBlur(event) {
      this.v$.enquiryValue.$reset();
      triggerValidate(event, this.v$.enquiryValue, 'upper');
    },
    handleClear() {
      this.clearInput();
      if (this.result.queryNum) {
        this.result = this.defaultResultObj();
      }
    },
    clearInput() {
      this.enquiryValue = null;
      this.v$.enquiryValue.$reset();
    },
    async search() {
      this.v$.enquiryValue.$reset();
      let validateRes = await this.v$.enquiryValue.$validate();
      if (!validateRes) return;

      this.result = this.defaultResultObj();
      let jwt = this.companyStore.jwt;
      let payload = {};
      payload[constants.expiryEnquiry.identifier[this.enquiryType]] = this.enquiryValue;

      let loadingInstance = loading.create("process");
      renewalAPI.postGetExpiryDate(jwt, payload).then((res) => {
        if (res.status == 200) {
          this.result.queryType = this.enquiryType;
          this.result.queryNum = this.enquiryValue;
          this.result.expiry = res.data;
          this.clearInput();
          
        } else {
          WarningAlert(this.$i18n.t("exceptions.noRecordFound"));
        }
      }).catch((err)=> {
        console.log(err);
        handleCompanyException(err);
      }).finally(() => {
        loadingInstance.close();
      });
    },
    defaultResultObj() {
      return {
        queryType: null,
        queryNum: null,
        expiry: null
      }
    },
    getSelectedTypeLabel(type) {
      return this.$i18n.tm("company.expiryEnquiry.type.options")[type].label;
    },
    formatDT(dateTime) {
      return moment(dateTime).format("DD/MM/YYYY")
    }
  }
}
</script>

<style scoped>
.toolbar,
.title {
  margin: 10px 20px;
}

.table-footer {
  margin-top: 20px;
}

.enquiry-value-input {
  width: 200px;
}
</style>
